<template>
  <modal-wrapper
    ref="modalRef"
    :header="title"
    :content-overflow-y="'visible'"
    :ok-button-label="t('generic.add')"
    @ok-button-click="handleModalOK"
    @show="getAdGroupMembers"
  >
    <p v-if="infoText">{{ infoText }}</p>
    <styled-multiselect
      v-model="selectedUser"
      :options="adGroupUsers"
      :custom-label="nameAndEmailLabel"
      :placeholder="t('adGroupModal.selectUser')"
      track-by="email"
      :loading="isLoadingUsers"
      :select-label="t('adGroupModal.pressEnterToSelect')"
      :show-no-results="false"
      :label="nameAndEmailLabel"
    >
    </styled-multiselect>
    <p v-if="isNoSelectedUserError" class="error-message">
      {{ t('adGroupModal.noUserSelected') }}
    </p>
  </modal-wrapper>
</template>

<script lang="ts" setup>
  import { ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useToast } from '../../composables/useToast';
  import axios, { AxiosResponse } from 'axios';
  import endpointsList from '../../helpers/endpointsList';
  import { AdGroup, IAdUserInternalFormat, IInspector } from 'types';
  import StyledMultiselect from '../../components/StyledMultiselect/StyledMultiselect.vue';
  import { useModalWrapper } from '../ModalWrapper';

  const props = defineProps<{
    group: AdGroup;
    title: string;
    infoText?: string;
    modalId?: string;
  }>();

  const emit = defineEmits<{ (e: 'selected-user', payload?: any): void }>();

  const { modalRef, show, hide } = useModalWrapper();
  defineExpose({ show, hide });

  const selectedUser = ref<boolean>(false);
  const adGroupUsers = ref<IAdUserInternalFormat[]>([]);
  const isLoadingUsers = ref<boolean>(false);
  const isNoSelectedUserError = ref<boolean>(false);

  const { t } = useI18n();
  const { createErrorToast } = useToast();

  async function getAdGroupMembers() {
    const groupEndpoint = getEndpointForGroup(props.group);

    try {
      const response: AxiosResponse<IAdUserInternalFormat[]> = await axios.get(
        groupEndpoint(props.group),
      );
      adGroupUsers.value = response.data;
    } catch {
      createErrorToast(t('adGroupModal.couldNotGetUsers'));
    }
  }

  function getEndpointForGroup(group: AdGroup): (group: AdGroup) => string {
    switch (group) {
      case AdGroup.INSPECTOR:
        return endpointsList.adIntegration
          .getAllUsersFromSupplierProjectAdGroup;
      case AdGroup.COWORKER:
        return endpointsList.adIntegration
          .getAllUsersFromSupplierProjectAdGroup;
      case AdGroup.DOCU_MANAGER:
        return endpointsList.adIntegration
          .getAllUsersFromSupplierProjectAdGroup;
      case AdGroup.DOCU_RESPONSIBLE:
        return endpointsList.adIntegration
          .getAllUsersFromDigitalizationProjectAdGroup;
      case AdGroup.WORKSPACE_OWNER:
        return endpointsList.adIntegration.getAllUsersFromWorkspacesAdGroup;
      default:
        throw new Error('Wrong group name passed to AdGroupModal');
    }
  }

  function nameAndEmailLabel({ firstName, lastName, email }: IInspector) {
    if (firstName === null && lastName === null) {
      firstName = t('adGroupModal.noData');
      lastName = '';
    } else if (firstName === null) {
      firstName = t('adGroupModal.noData');
    } else if (lastName === null) {
      lastName = t('adGroupModal.noData');
    }

    if (email) {
      return `${firstName} ${lastName} — [${email}]`;
    } else {
      return `${firstName} ${lastName}`;
    }
  }

  function cleanModal() {
    selectedUser.value = false;
    isLoadingUsers.value = false;
    isNoSelectedUserError.value = false;
  }

  function handleModalOK() {
    if (selectedUser.value) {
      isNoSelectedUserError.value = false;
      emit('selected-user', selectedUser.value);

      hide();
      cleanModal();
    } else {
      isNoSelectedUserError.value = true;
    }
  }

  watch(selectedUser, () => {
    if (selectedUser.value) {
      isNoSelectedUserError.value = false;
    }
  });
</script>

<style scoped lang="scss">
  .error-message {
    color: red;
    font-weight: bold;
    text-align: center;
    margin-top: 20px;
  }
</style>
